import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../components/Modal';
import FormField from '../../components/FormField';
import Badge from '../../components/Badge';
import { useSchemas, useMyDIDs } from '../../api/hooks';
import { apiFetch } from '../../api/client';
import { encodePathSegment } from '../../lib/url';
import SchemaStatusDialog from '../../components/SchemaStatusDialog';

interface SchemaField {
  name: string;
  type: string;
  required: boolean;
}

const fieldTypes = ['string', 'number', 'boolean', 'date', 'uri', 'did', 'array'];

export default function SchemaManagement() {
  const { data: myDIDs, loading: didsLoading } = useMyDIDs();
  // A schema is authored by a DID, so one must exist before the form is worth
  // filling in. Resolved here (not only inside handleCreate) so the modal can
  // say so up front and keep the Create button disabled until it's true.
  const authorDID = (myDIDs ?? []).find((d) => d.status === 'active')?.did ?? (myDIDs ?? [])[0]?.did ?? '';
  const hasAuthorDID = authorDID !== '';
  // "My Schemas" means the caller's own — authored by any of their DIDs, not
  // just the one new schemas are created under. The network-wide view lives on
  // Network > Schemas.
  const { data: schemas, loading: schemasLoading, error, refetch } = useSchemas('mine');
  const loading = didsLoading || schemasLoading;
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  // Schema whose active/retired status is being changed, plus in-flight + error
  // state for that confirmation.
  type SchemaRow = NonNullable<typeof schemas>[number];
  const [statusTarget, setStatusTarget] = useState<SchemaRow | null>(null);
  // Shown after a deactivate-and-revoke, which changes more than the table shows.
  const [statusNotice, setStatusNotice] = useState('');
  const { t } = useTranslation(['issuer', 'schemas', 'common']);

  const [newName, setNewName] = useState('');
  const [newVersion, setNewVersion] = useState('1.0.0');
  const [newDesc, setNewDesc] = useState('');
  const [newFields, setNewFields] = useState<SchemaField[]>([
    { name: '', type: 'string', required: true },
  ]);

  const selectedSchema = (schemas ?? []).find((s) => s.id === selected) ?? null;

  const addField = () => setNewFields([...newFields, { name: '', type: 'string', required: false }]);
  const removeField = (idx: number) => setNewFields(newFields.filter((_, i) => i !== idx));
  const updateField = (idx: number, patch: Partial<SchemaField>) =>
    setNewFields(newFields.map((f, i) => (i === idx ? { ...f, ...patch } : f)));

  const previewJson = {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    $id: `urn:uuid:schema-${Date.now()}`,
    title: newName,
    version: newVersion,
    description: newDesc,
    type: 'object',
    properties: Object.fromEntries(
      newFields.filter((f) => f.name).map((f) => [f.name, { type: f.type }])
    ),
    required: newFields.filter((f) => f.required && f.name).map((f) => f.name),
  };

  const handleCreate = async () => {
    setCreating(true);
    setCreateError('');
    const activeDID = (myDIDs ?? []).find((d) => d.status === 'active')?.did ?? (myDIDs ?? [])[0]?.did ?? '';
    if (!activeDID) {
      setCreateError('You need an active DID to create a schema. Please create a DID first.');
      setCreating(false);
      return;
    }
    // Mirrors the server-side rules so the user sees the problem before the
    // round-trip. Fields with a blank name are dropped when the JSON Schema is
    // built, so a schema with no named field would otherwise submit silently.
    const namedFields = newFields.filter((f) => f.name.trim());
    if (namedFields.length === 0) {
      setCreateError('Add at least one schema field with a name.');
      setCreating(false);
      return;
    }
    const duplicate = namedFields
      .map((f) => f.name.trim().toLowerCase())
      .find((name, i, all) => all.indexOf(name) !== i);
    if (duplicate) {
      setCreateError(`Duplicate field name "${duplicate}". Field names must be unique.`);
      setCreating(false);
      return;
    }
    if (!/^\d+(\.\d+){1,2}$/.test(newVersion.trim())) {
      setCreateError('Version must look like 1.0 or 1.0.0 (numbers and dots only).');
      setCreating(false);
      return;
    }
    try {
      const schemaObj = {
        $schema: 'https://json-schema.org/draft/2020-12/schema',
        title: newName,
        version: newVersion,
        description: newDesc,
        type: 'object',
        properties: Object.fromEntries(
          newFields.filter((f) => f.name).map((f) => [f.name, { type: f.type }])
        ),
        required: newFields.filter((f) => f.required && f.name).map((f) => f.name),
      };
      await apiFetch('/credentials/schemas', {
        method: 'POST',
        body: JSON.stringify({
          name: newName,
          version: newVersion,
          author: activeDID,
          schema: schemaObj,
        }),
      });
      setShowCreate(false);
      setNewName('');
      setNewVersion('1.0.0');
      setNewDesc('');
      setNewFields([{ name: '', type: 'string', required: true }]);
      refetch();
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : t('schemas:management.create.errorFallback'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-8">
      {statusNotice && (
        <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <span>{statusNotice}</span>
          <button onClick={() => setStatusNotice('')} className="text-emerald-600 hover:text-emerald-800" aria-label="Dismiss">
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
          </button>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('schemas:management.title')}</h1>
          <p className="mt-1 text-sm text-text-muted">{t('schemas:management.subtitle')}</p>
          <p className="mt-0.5 text-xs text-text-faint">
            Only schemas you authored. To see every issuer's schemas, go to Network → Schemas.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add_circle</span>
          {t('schemas:management.createSchema')}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="text-center py-20 text-red-500">{t('common:errors.loadSchemasFailed')}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
            {[
              { label: t('schemas:management.stats.total'), value: String((schemas ?? []).length), icon: 'schema' },
              { label: t('schemas:management.stats.active'), value: String((schemas ?? []).filter((s) => s.status === 'active').length), icon: 'check_circle' },
              { label: t('schemas:management.stats.credentials'), value: (schemas ?? []).reduce((a, s) => a + (s.credentialCount ?? s.credentials ?? 0), 0).toLocaleString(), icon: 'description' },
              { label: t('schemas:management.stats.draft'), value: String((schemas ?? []).filter((s) => s.status === 'draft').length), icon: 'edit_note' },
            ].map((stat) => (
              <div key={stat.label} className="stat-card">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-text-faint" style={{ fontSize: 16 }}>{stat.icon}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">{stat.label}</span>
                </div>
                <span className="mt-2 block text-3xl font-bold text-text-main">{stat.value}</span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <div className="overflow-hidden rounded-xl border border-border-subtle bg-white shadow-sm">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border-subtle bg-slate-50/50">
                      <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('schemas:management.table.schema')}</th>
                      <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('schemas:management.table.version')}</th>
                      <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('schemas:management.table.credentials')}</th>
                      <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('schemas:management.table.status')}</th>
                      <th className="px-6 py-3 text-right text-[11px] font-extrabold uppercase tracking-widest text-text-faint">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(schemas ?? []).map((schema) => (
                      <tr
                        key={schema.id}
                        onClick={() => setSelected(schema.id)}
                        className={`cursor-pointer border-b border-border-subtle transition-colors hover:bg-slate-50/60 ${
                          selected === schema.id ? 'bg-blue-50/50' : ''
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex size-10 items-center justify-center rounded-lg bg-blue-50">
                              <span className="material-symbols-outlined text-primary" style={{ fontSize: 20 }}>schema</span>
                            </div>
                            <div>
                              <p className="text-sm font-bold tracking-tight text-text-main">{schema.name}</p>
                              <p className="text-[10px] text-text-faint">{schema.description ?? ''}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs font-bold text-text-main">v{schema.version}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-bold text-text-main">{(schema.credentialCount ?? schema.credentials ?? 0).toLocaleString()}</span>
                        </td>
                        <td className="px-6 py-4">
                          <Badge
                            variant={schema.status === 'active' ? 'success' : schema.status === 'draft' ? 'neutral' : 'warning'}
                          >
                            {schema.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => setStatusTarget(schema)}
                            title={schema.deactivated ? 'Reactivate schema' : 'Deactivate schema (blocks new issuance)'}
                            className="rounded-lg px-2.5 py-1 text-xs font-bold text-text-faint transition-colors hover:bg-slate-100 hover:text-text-main"
                          >
                            {schema.deactivated ? 'Reactivate' : 'Deactivate'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              {selectedSchema ? (
                <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center rounded-lg bg-blue-50">
                      <span className="material-symbols-outlined text-primary" style={{ fontSize: 20 }}>schema</span>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-text-main">{selectedSchema.name}</p>
                      <p className="text-[10px] text-text-faint">v{selectedSchema.version}</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <span className="text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('schemas:management.detail.fields')}</span>
                    <div className="mt-2 overflow-hidden rounded-lg border border-border-subtle">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-slate-50">
                            <th className="px-3 py-2 text-left font-extrabold uppercase tracking-widest text-text-faint" style={{ fontSize: 9 }}>{t('schemas:management.detail.name')}</th>
                            <th className="px-3 py-2 text-left font-extrabold uppercase tracking-widest text-text-faint" style={{ fontSize: 9 }}>{t('schemas:management.detail.type')}</th>
                            <th className="px-3 py-2 text-left font-extrabold uppercase tracking-widest text-text-faint" style={{ fontSize: 9 }}>{t('schemas:management.detail.required')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedSchema.fields.map((f) => (
                            <tr key={f.name} className="border-t border-border-subtle">
                              <td className="px-3 py-2 font-bold text-text-main">{f.name}</td>
                              <td className="px-3 py-2">
                                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">{f.type}</span>
                              </td>
                              <td className="px-3 py-2">
                                {f.required ? (
                                  <span className="material-symbols-outlined text-emerald-500" style={{ fontSize: 16 }}>check</span>
                                ) : (
                                  <span className="material-symbols-outlined text-slate-300" style={{ fontSize: 16 }}>remove</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {selectedSchema.versions && selectedSchema.versions.length > 0 && (
                    <div>
                      <span className="text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('schemas:management.detail.versionHistory')}</span>
                      <div className="mt-2 space-y-2">
                        {selectedSchema.versions.map((v) => (
                          <div key={v.version} className="rounded-lg border border-border-subtle p-3">
                            <div className="flex items-center justify-between">
                              <span className="font-mono text-xs font-bold text-primary">v{v.version}</span>
                              <span className="text-[10px] text-text-faint">{v.date}</span>
                            </div>
                            <p className="mt-1 text-[10px] text-text-muted">{v.changes}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex h-64 items-center justify-center rounded-xl border border-border-subtle bg-white p-6 text-center shadow-sm">
                  <div>
                    <span className="material-symbols-outlined mb-2 text-text-faint" style={{ fontSize: 40 }}>schema</span>
                    <p className="text-sm text-text-faint">{t('schemas:management.detail.selectHint')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title={t('schemas:management.create.title')} wide>
        <div className="space-y-5">
          {hasAuthorDID ? (
            <p className="text-xs text-text-faint">
              {t('schemas:management.create.authorPrefix', { defaultValue: 'Will be authored by' })}{' '}
              <span className="font-mono" title={authorDID}>{authorDID}</span>
            </p>
          ) : (
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <span className="material-symbols-outlined shrink-0" style={{ fontSize: 18 }}>info</span>
              <div>
                <p className="font-semibold">
                  {t('schemas:management.create.needDidTitle', { defaultValue: 'A DID is required before you can create a schema' })}
                </p>
                <p className="mt-0.5 text-xs">
                  {t('schemas:management.create.needDidBody', { defaultValue: 'Schemas are authored by a DID you control. Create one under My DIDs, then come back — the form below stays disabled until then.' })}
                </p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField label={t('schemas:management.create.nameLabel')} placeholder={t('schemas:management.create.namePlaceholder')} value={newName} onChange={(e) => setNewName((e.target as HTMLInputElement).value)} />
            <FormField label={t('schemas:management.create.versionLabel')} placeholder={t('schemas:management.create.versionPlaceholder')} value={newVersion} onChange={(e) => setNewVersion((e.target as HTMLInputElement).value)} />
          </div>
          <FormField as="textarea" label={t('schemas:management.create.descriptionLabel')} placeholder={t('schemas:management.create.descriptionPlaceholder')} value={newDesc} onChange={(e) => setNewDesc((e.target as HTMLTextAreaElement).value)} />

          <div>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-bold text-text-muted">{t('schemas:management.create.fieldsLabel')}</span>
              <button onClick={addField} className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:text-primary-dark">
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>add</span>
                {t('schemas:management.create.addField')}
              </button>
            </div>
            <div className="space-y-3">
              {newFields.map((field, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="flex-1">
                    <input
                      className="w-full rounded-lg border border-border-stroke bg-white px-3 py-2 text-sm text-text-main placeholder:text-text-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      placeholder={t('schemas:management.create.fieldNamePlaceholder')}
                      value={field.name}
                      onChange={(e) => updateField(idx, { name: e.target.value })}
                    />
                  </div>
                  <select
                    className="rounded-lg border border-border-stroke bg-white px-3 py-2 text-sm text-text-main focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    value={field.type}
                    onChange={(e) => updateField(idx, { type: e.target.value })}
                  >
                    {fieldTypes.map((tp) => (
                      <option key={tp} value={tp}>{tp}</option>
                    ))}
                  </select>
                  <label className="flex items-center gap-1.5 text-xs text-text-muted">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300"
                      checked={field.required}
                      onChange={(e) => updateField(idx, { required: e.target.checked })}
                    />
                    {t('schemas:management.create.required')}
                  </label>
                  <button
                    onClick={() => removeField(idx)}
                    className="flex size-8 items-center justify-center rounded-lg text-text-faint hover:bg-red-50 hover:text-red-500"
                    disabled={newFields.length === 1}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 18 }}>delete</span>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {createError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{createError}</div>
          )}

          <div className="flex gap-3">
            <button className="btn-secondary" onClick={() => setShowPreview(true)}>
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>code</span>
              {t('schemas:management.create.previewJson')}
            </button>
            <button
              className="btn-primary ml-auto"
              onClick={handleCreate}
              disabled={creating || !hasAuthorDID}
              title={hasAuthorDID ? undefined : t('schemas:management.create.needDidTitle', { defaultValue: 'A DID is required before you can create a schema' })}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 18 }}>check_circle</span>
              {creating ? t('schemas:management.create.creating') : t('schemas:management.create.create')}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={showPreview} onClose={() => setShowPreview(false)} title={t('schemas:management.previewModalTitle')}>
        <pre className="overflow-x-auto rounded-lg border border-border-subtle bg-slate-50 p-4 font-mono text-xs text-text-main">
          {JSON.stringify(previewJson, null, 2)}
        </pre>
      </Modal>

      <SchemaStatusDialog
        target={statusTarget ? { ...statusTarget, credentialCount: statusTarget.credentialCount ?? statusTarget.credentials ?? 0 } : null}
        onClose={() => setStatusTarget(null)}
        onDone={(msg) => { setStatusTarget(null); setStatusNotice(msg ?? ''); refetch(); }}
      />
    </div>
  );
}
